<i18n src="./resources/locales.json"></i18n>

<template>
  <EcsDashboardCard :disabled="disabled" :dark="$vuetify.theme.dark" @resize="expanded => $emit('resize', expanded)">
    <div class="title-container">
      <div v-once class="card-title">
        {{ $t('businessProcessSummaryTitle') }}
      </div>
    </div>

    <div class="bp-multi-select">
      <div class="bp-selection">
        <v-select
          v-model="selectedBP"
          :items="businessProcessesStats"
          :label="$t('businessProcess')"
          item-value="businessProcessId"
          item-text="businessProcessName"
          class="bp-select"
          dense
          multiple
          :disabled="!!onlyForBusinessProcessId"
          @change="changeSelectedBP"
        />
      </div>
    </div>

    <div class="card-graph-container">
      <div ref="graph" class="graph" />

      <div class="qs-legend">
        <div class="legend-title">
          {{ $t('interactionsWaiting') }}
          {{ getTotalInteractionsWaiting }}
        </div>
        <div v-for="(data, i) in dataset" :id="'qs-legend-' + i" :key="i">
          <span class="bullet" :style="{ backgroundColor: data.color }"></span>
          <span class="legend-value">{{ data.value }}</span>
          <span class="legend-description">{{ data.i18n }}</span>
        </div>
      </div>
    </div>

    <div class="total-stat">
      <div class="total-stat-count">
        {{ kpiFormat(longestInteraction.businessProcessLongestWaitingTime.value, 'duration') }}
      </div>
      <div v-once class="total-stat-description">
        {{ $t('longestWaitingInteraction') }}
      </div>
    </div>

    <div class="total-stat">
      <div class="total-stat-count">
        {{ kpiFormat(getWaitingEmails, 'number') }}
      </div>
      <div v-once class="total-stat-description">
        {{ $t('emailWaitingInteractions') }}
      </div>
    </div>

    <div class="total-stat">
      <div class="total-stat-count">
        {{ getTotalCallbacks }}
      </div>
      <div v-once class="total-stat-description">
        {{ $t('callbacks') }}
      </div>
    </div>

    <div class="clear"></div>

    <div class="footer-info">
      {{ $t('lastReset') }}
      {{ lastReset }}
    </div>
  </EcsDashboardCard>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import EcsDashboardCard from '@/components/ecs-dashboard-card/EcsDashboardCard';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';
import PieGraph from '@/components/ecs-queue-status-card/d3-pie-graph/d3-pie-graph';

const DEFAULT_BP = {
  businessProcessLoggedInAgents: {},
  businessProcessWaitingInteractions: {},
  businessProcessWaitingDeferrableInteractions: {},
  businessProcessLongestWaitingTime: {},
  businessProcessServiceLevel: {},
  businessProcessASA: {},
  businessProcessAnsweredInteractions: {},
  businessProcessAbandonedInteractions: {},
  businessProcessOverflowInteractions: {},
  businessProcessAbandonedPercentage: {},
  businessProcessAverageAgentHandlingTime: {}
};

export default {
  name: 'EcsQueueBusinessProcessSummaryCard',

  components: {
    EcsDashboardCard
  },

  mixins: [EcsKpiFormatter],

  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    onlyForBusinessProcessId: {
      type: String,
      default: ''
    },
    startBusinessProcessId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      dataset: [],
      expanded: false,
      search: '',
      currentBP: DEFAULT_BP,
      selectedBusinessProcess: [],
      selectedBP: [],
      longestInteraction: DEFAULT_BP,
      callbacks: 0,
      deferrables: 0,
      graph: null
    };
  },

  computed: {
    ...mapGetters('STATS', ['AGENTS_STATS', 'BUSINESS_PROCESSES_STATS']),

    businessProcessesStats() {
      const bps = this.BUSINESS_PROCESSES_STATS;
      return bps.sort(this.sortByBpName) || [DEFAULT_BP];
    },
    lastReset() {
      return moment((this.selectedBusinessProcess || {}).lastResetTime).fromNow();
    },
    getTotalCallbacks() {
      let totalCallBack = 0;
      this.selectedBusinessProcess.forEach(bp => {
        totalCallBack += this.getBpPropWithBp(bp, 'businessProcessRequestCallbackInteractions') || 0;
      });
      return totalCallBack;
    },
    getWaitingEmails() {
      let totalEmails = 0;
      this.selectedBusinessProcess.forEach(bp => {
        totalEmails += this.getBpPropByType(bp, 'businessProcessWaitingDeferrableInteractions', 'E-mail') || 0;
      });
      return totalEmails;
    },
    getTotalInteractionsWaiting() {
      let totalInteractions = 0;
      this.selectedBusinessProcess.forEach(bp => {
        totalInteractions += this.getBpPropWithBp(bp, 'businessProcessWaitingInteractions') || 0;
      });
      return totalInteractions;
    }
  },

  watch: {
    businessProcessesStats() {
      this.initialize();
    }
  },

  created() {
    this.initialize();
  },

  methods: {
    getBpPropByType(bp, key, type) {
      if (!bp) {
        return null;
      }
      if (!this.selectedBP.includes(bp.businessProcessId)) {
        return null;
      }
      return (
        (bp[key] || {}).dimensionsValues.find(item => {
          return item.name.toLowerCase() === type.toLowerCase();
        }) || {}
      ).value;
    },

    getBpPropWithBp(bp, key) {
      if (!bp) {
        return null;
      }
      if (!this.selectedBP.includes(bp.businessProcessId)) {
        return null;
      }
      return (bp[key] || {}).value;
    },

    async initialize() {
      if (!this.businessProcessesStats.length) {
        return;
      }
      this.selectedBP =
        this.startBusinessProcessId && this.startBusinessProcessId.length > 0
          ? this.startBusinessProcessId.split(',')
          : this.businessProcessesStats.map(item => item.businessProcessId);
      this.selectedBusinessProcess = this.businessProcessesStats;
      await this.$nextTick();
      this.graph = new PieGraph(this.$refs.graph, []);
      this.updateGraph();
      this.updateLongestInteraction();
    },

    sortByBpName(bpA, bpB) {
      if (bpA.businessProcessName > bpB.businessProcessName) {
        return 1;
      }
      if (bpB.businessProcessName > bpA.businessProcessName) {
        return -1;
      }
      return 0;
    },

    async updateGraph() {
      this.dataset = [
        {
          i18n: this.$t('chat'),
          value: this.getTotalWaitingInteractionsByType('chat') || 0,
          color: '#4ea96d'
        },
        {
          i18n: this.$t('telephony'),
          value: this.getTotalWaitingInteractionsByType('telephony'),
          color: '#f44336'
        },
        {
          i18n: this.$t('sms'),
          value: this.getTotalWaitingInteractionsByType('sms'),
          color: '#ffc107'
        }
      ];

      await this.$nextTick();
      if (this.graph && this.graph.updateDataset) {
        this.graph.updateDataset(this.dataset);
      }
      await this.$nextTick();
      this.$forceUpdate();
    },

    updateLongestInteraction() {
      this.longestInteraction = DEFAULT_BP;
      this.selectedBusinessProcess.forEach(bp => {
        if (this.getBpPropWithBp(bp, 'businessProcessLongestWaitingTime') !== '00:00:00') {
          this.longestInteraction = bp;
        }
      });
    },

    getTotalWaitingInteractionsByType(type) {
      let totalInteractions = 0;
      this.selectedBusinessProcess.forEach(bp => {
        totalInteractions += this.getBpPropByType(bp, 'businessProcessWaitingInteractions', type) || 0;
      });
      return totalInteractions;
    },

    changeSelectedBP() {
      this.selectedBusinessProcess = this.businessProcessesStats.filter(bp =>
        this.selectedBP.some(item => item === bp.businessProcessId)
      );
      this.$emit('saveCardStartingBusinessProcess', this.selectedBP.join());
      this.updateGraph();
      this.updateLongestInteraction();
    }
  }
};
</script>

<style src="./scoped.css" scoped></style>
